import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { fileUrl } from "@/components/EntityAvatar";
import { Upload, Trash2, Loader2, ExternalLink, Download } from "lucide-react";

// Tüm entity'lerde ortak evrak (özlük/ruhsat/sözleşme vb.) yönetimi — çoklu tür + bitiş tarihi + görüntüle/indir/sil + toplu ZIP.
export const DOC_CATEGORIES = [
  ["kimlik", "Kimlik / Nüfus"], ["sozlesme", "Sözleşme"], ["sgk", "SGK Belgesi"],
  ["diploma", "Diploma / Sertifika"], ["ehliyet", "Ehliyet"], ["saglik", "Sağlık Raporu"],
  ["ruhsat", "Ruhsat"], ["sigorta", "Sigorta / Kasko"], ["muayene", "Muayene / Fenni"],
  ["vergi", "Vergi Levhası"], ["imza", "İmza Sirküleri"], ["fatura", "Fatura / İrsaliye"],
  ["cari", "Cari / Ekstre"], ["diger", "Diğer"],
];
const CAT_LABEL = Object.fromEntries(DOC_CATEGORIES);

const expiryInfo = (exp) => {
  if (!exp) return null;
  const d = new Date(exp);
  if (isNaN(d)) return null;
  const days = Math.round((d - new Date(new Date().toDateString())) / 86400000);
  if (days < 0) return { cls: "bg-rose-100 text-rose-700", label: `Süresi ${-days}g önce doldu` };
  if (days <= 30) return { cls: "bg-amber-100 text-amber-800", label: `${days}g kaldı` };
  return { cls: "bg-slate-100 text-slate-500", label: `Bitiş ${exp}` };
};

export function DocumentManager({ docs = [], onAdd, onDelete, canWrite = true, zipPath, testid = "docmgr" }) {
  const [cat, setCat] = useState("diger");
  const [exp, setExp] = useState("");
  const [busy, setBusy] = useState(false);
  const [zipping, setZipping] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Dosya 15MB'den küçük olmalı"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const up = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onAdd({ name: f.name, category: cat, expiry: exp, url: up.data.url, path: up.data.path });
      setExp("");
    } catch (er) {
      toast.error(apiError(er.response?.data?.detail) || "Belge yüklenemedi");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const downloadZip = async () => {
    setZipping(true);
    try {
      const res = await api.get(zipPath, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "evraklar.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (er) {
      toast.error("Evraklar indirilemedi");
    } finally {
      setZipping(false);
    }
  };

  return (
    <div data-testid={testid} className="space-y-2">
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-2 ring-1 ring-inset ring-slate-200">
          <select data-testid={`${testid}-cat`} value={cat} onChange={(e) => setCat(e.target.value)}
            className="h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
            {DOC_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate-400">Bitiş:</span>
            <input data-testid={`${testid}-expiry`} type="date" value={exp} onChange={(e) => setExp(e.target.value)}
              className="h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green" title="Sözleşme/sigorta bitiş tarihi (opsiyonel) — yaklaşınca panelde uyarılır" />
          </div>
          <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-brand-green px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-green-600 ${busy ? "opacity-60" : ""}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Evrak Ekle
            <input data-testid={`${testid}-upload`} type="file" className="hidden" disabled={busy}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" onChange={upload} />
          </label>
          {zipPath && docs.length > 0 && (
            <button data-testid={`${testid}-zip`} onClick={downloadZip} disabled={zipping}
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
              {zipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Tümünü ZIP indir
            </button>
          )}
        </div>
      )}
      {docs.length === 0 ? (
        <p data-testid={`${testid}-empty`} className="rounded-md bg-white px-3 py-4 text-center text-xs text-slate-400 ring-1 ring-inset ring-slate-100">Henüz evrak eklenmedi.</p>
      ) : (
        <div className="space-y-1.5" data-testid={`${testid}-list`}>
          {docs.map((doc) => {
            const ei = expiryInfo(doc.expiry);
            return (
              <div key={doc.id} data-testid={`${testid}-item-${doc.id}`} className="flex items-center gap-2 rounded-md bg-white px-3 py-2 ring-1 ring-inset ring-slate-200">
                <span className="shrink-0 rounded-full bg-brand-green/10 px-2 py-0.5 text-[10px] font-bold text-brand-green">{CAT_LABEL[doc.category] || doc.category || "Belge"}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700">{doc.name}</p>
                  {doc.uploaded_at && <p className="text-[10px] text-slate-400">{String(doc.uploaded_at).slice(0, 10)}{doc.uploaded_by ? ` · ${doc.uploaded_by}` : ""}</p>}
                </div>
                {ei && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${ei.cls}`}>{ei.label}</span>}
                <a data-testid={`${testid}-open-${doc.id}`} href={fileUrl(doc.url)} target="_blank" rel="noreferrer" className="shrink-0 rounded-sm p-1.5 text-sky-600 hover:bg-sky-50" title="Görüntüle / İndir"><ExternalLink className="h-4 w-4" /></a>
                {canWrite && onDelete && <button data-testid={`${testid}-del-${doc.id}`} onClick={() => onDelete(doc.id)} className="shrink-0 rounded-sm p-1.5 text-red-500 hover:bg-red-50" title="Sil"><Trash2 className="h-4 w-4" /></button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DocumentManager;
